import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'
import type { ButtonSize } from './Button'

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Required, because an icon on its own says nothing to a screen reader and
   *  nothing in a tooltip. There is no unlabelled icon button. */
  label: string
  /** The glyph box — 20px at `md`, 16px at `sm`. Never the hit area, which is
   *  44px at both sizes: that is the whole point of having a size at all. */
  size?: ButtonSize
  /** `overlay` sits on a photograph and carries its own scrim. */
  tone?: 'default' | 'overlay'
  children: ReactNode
}

/**
 * A button that is only an icon. 44×44 always.
 *
 * `aria-pressed` is not a prop here but it is read by `ui.css`: a save heart is
 * an IconButton the caller keeps pressed, and the fill-and-pop motion D6 asks
 * for is attached to the attribute rather than to a `saved` prop, so any
 * toggle gets it and no second component is needed to hold one animation.
 */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, size = 'md', tone = 'default', className = '', children, ...rest },
  ref,
) {
  const classes = [
    'u-iconbtn', `u-iconbtn--${size}`,
    tone === 'default' ? '' : `u-iconbtn--${tone}`, className,
  ].filter(Boolean).join(' ')

  return (
    <button ref={ref} type="button" className={classes} aria-label={label} {...rest}>
      {children}
    </button>
  )
})
